import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useForm, useWatch } from "react-hook-form";
import { ProgressBar } from "react-loader-spinner";
import useChapterActionIdMatch from "../../../hooks/useChapterActionIdMatch";

import SelectWithSearch from "../../GeneralComponents/componentsForForm/selectWithSearch/SelectWithSearch";
import InputForForm from "../../GeneralComponents/componentsForForm/inputForForm/InputForForm";
import NotificationWindow from "../../GeneralComponents/NotificationWindow/NotificationWindow";
import TableComponent from "./../../GeneralComponents/SearchTable/tableComponent";

import {
  formatDate,
  getInputsInspectorWorkStat,
  setInputsInspectorWorkStat,
  tableHeaderCreator,
} from "./optionsForIncspectorWorkStat";

import { fetchWithToken } from "../../../interceptors/fetchInterceptor";

import { API_GET_INSPECTOR_WORK_STAT, MAIN_URL, PORT_FOR_REPORT } from "../../../constants/constants";

import styles from "./InspectorWorkStat.module.css";
import ToolBlock from "../../AdministrativeProcedures/commonComponents/ToolBlock/ToolBlock";

export default function InspectorWorkStat() {
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
    setValue,
    watch,
  } = useForm({ mode: "onChange" });

  const [inputOptions, setInputOptions] = useState(getInputsInspectorWorkStat());
  const [inputValue, setInputValue] = useState({});
  const [directoryLoad, setDirectoryLoad] = useState(false);
  const [showNotificationWindow, setShowNotificationWindow] = useState(false);
  const [messageNotificationWindow, setMessageNotificationWindow] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [tableHeader, settTableHeader] = useState([]);
  const [dataRangeError, setDataRangeError] = useState("");
  const [disablePrint, setDisablePrint] = useState(true);

  const usersLib = useSelector((state) => {
    const { dictionaryReducer } = state;
    return dictionaryReducer.usersLibrary.map((item) => {
      return { value: item.userid, label: item.name, key: "inspector" };
    });
  });
  const sortedMenuNames = useSelector((state) => {
    const { roleControllerReducer } = state;
    return roleControllerReducer.sortedMenuNames;
  });

  const createAccess = useChapterActionIdMatch(
    sortedMenuNames?.INSPECTOR_WORKSTAT?.createAReportOrGraph?.chapterActionId,
  );
  const printAccess = useChapterActionIdMatch(
    sortedMenuNames?.INSPECTOR_WORKSTAT?.exportReport?.chapterActionId,
  );

  useEffect(() => {
    setInputsInspectorWorkStat("firstInit", usersLib);
    setInputOptions(getInputsInspectorWorkStat());
  }, [directoryLoad]);

  useEffect(() => {
    if (Boolean(usersLib.length) && !directoryLoad) {
      setDirectoryLoad(true);
    }
  }, [usersLib]);

  const handleDataRange = (data) => {
    if (!!data.date1 && !!data.date2) {
      switch (true) {
        case Math.floor((new Date(data.date2) - new Date(data.date1)) / (1000 * 60 * 60 * 24)) > 365:
          setDataRangeError("Максимальный промежуток запрашиваемого отчёта находится в пределах 1 года.");
          return true;
        case Math.floor((new Date(data.date2) - new Date(data.date1)) / (1000 * 60 * 60 * 24)) < 0:
          setDataRangeError(
            'Дата, установленная в "Период отчета по" не может быть меньше даты, установленной в "Период отчета с".',
          );
          return true;
        case new Date(data.date1) > new Date():
          setDataRangeError('Дата, установленная в "Период отчета с" не может быть больше текущей даты.');
          return true;
        case new Date(data.date2) > new Date():
          setDataRangeError('Дата, установленная в "Период отчета по" не может быть больше текущей даты.');
          return true;
        default:
          setDataRangeError("");
          return false;
      }
    }
  };

  const handleClose = () => {
    setShowNotificationWindow(false);
  };

  const handleClearData = (e) => {
    e.preventDefault();
    reset();
    setData([]);
    setIsLoading(false);
    setDataRangeError("");
  };

  const fetchPageData = () => null;

  //ToDo перенести запрос в redux
  const resolveSubmit = async (data) => {
    const getData = async () => {
      const request = await fetchWithToken(
        MAIN_URL +
          PORT_FOR_REPORT +
          API_GET_INSPECTOR_WORK_STAT +
          "?" +
          new URLSearchParams({
            date1: data.date1,
            date2: data.date2,
            inspectors: data.inspector,
          }),
      );
      if (request.status !== 200) {
        const responseData = await request.text();
        setData([]);
        setIsLoading(false);
        setMessageNotificationWindow(responseData);
        setShowNotificationWindow(!showNotificationWindow);
      } else {
        const response = await request.json();
        setIsLoading(false);
        setData([...response.slice(1, response.length)]);
        setDisablePrint(false);
        setInputValue(data);
        settTableHeader(tableHeaderCreator(response[0]));
      }
    };
    if (!handleDataRange(data)) {
      setData([]);
      setIsLoading(true);
      getData();
    }
  };

  const watchedFields = useWatch({ control });

  useEffect(() => {
    setDisablePrint(true);
  }, [watchedFields]);

  return (
    <div className={styles["content-container"]}>
      <h2 className={styles["main-text"]}>Статистика работы инспектора</h2>
      <form
        className={styles["form-inputs"]}
        onSubmit={handleSubmit(resolveSubmit)}>
        {!!dataRangeError && <div className={styles["range-block"]}>{dataRangeError}</div>}
        <div className={styles["area-inputs"]}>
          {Object.values(inputOptions).map((option) => {
            switch (true) {
              case option.type === "selectWithSearch": {
                return (
                  <SelectWithSearch
                    key={option.key}
                    externalStyles={styles}
                    inputProps={option}
                    control={control}
                    errors={errors}
                    setValue={setValue}
                    isMulti
                  />
                );
              }
              default:
                return (
                  <InputForForm
                    key={option.key}
                    externalStyles={styles}
                    register={register}
                    inputProps={option}
                    errors={errors}
                    setValue={setValue}
                  />
                );
            }
          })}
        </div>
        <div className={styles["buttons-container"]}>
          {createAccess && <button className={styles["add-btn"]}>Создать</button>}
          <button
            onClick={(e) => handleClearData(e)}
            className={styles["close-btn"]}>
            Очистить
          </button>
        </div>
      </form>
      <ToolBlock
        data={disablePrint ? [] : data}
        printAccess={printAccess}
        searchParams={inputValue}
      />
      {data.length !== 0 && (
        <>
          <TableComponent
            columns={tableHeader}
            data={data}
            fetchData={fetchPageData}
            onRowSelect={fetchPageData}
            totalPages={1}
            selectedPage={0}
            isPaginated={false}
            pageSize={data.length}
            headerName={`Отчет по работе ${
              inputValue?.inspector.length > 1 ? "инспекторов" : "инспектора"
            } за ${formatDate(inputValue?.date1)} - ${formatDate(inputValue?.date2)}`}
          />
        </>
      )}
      {isLoading && (
        <div className={"d-flex flex-column align-items-center"}>
          <ProgressBar
            height="80"
            width="80"
            ariaLabel="progress-bar-loading"
            borderColor="#D2AF8E"
            barColor="#BC8653"
          />
        </div>
      )}
      <NotificationWindow
        isError={showNotificationWindow}
        show={showNotificationWindow}
        setShow={setShowNotificationWindow}
        headerText={"Уведомление"}
        messageText={messageNotificationWindow}
        onClose={handleClose}
      />
    </div>
  );
}
